'use client';

import { useState, useEffect, useCallback } from 'react';
import Header from '@/components/common/Header';
import PerformanceMetrics from './PerformanceMetrics';
import LiveTicker from './LiveTicker';
import FilterBar from './FilterBar';
import SignalsList from './SignalsList';
import CommunityActivity from './CommunityActivity';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import { realtimeService } from '@/services/realtime.service';
import { tradingSignalsService } from '@/services/trading-signals.service';
import { useAuth } from '@/contexts/AuthContext';

interface Signal {
  id: string;
  pair: string;
  type: 'BUY' | 'SELL';
  entryPrice: number;
  targetPrice: number;
  stopLoss: number;
  confidence: number;
  timeframe: string;
  status: 'ACTIVE' | 'COMPLETED' | 'STOPPED';
  profit?: number;
  timestamp: string;
}

interface FilterState {
  status: string;
  type: string;
  timeframe: string;
  sortBy: string;
}

interface LiveSignalsDashboardInteractiveProps {
  isFreeUser?: boolean;
}

const LiveSignalsDashboardInteractive = ({ isFreeUser = false }: LiveSignalsDashboardInteractiveProps) => {
  const { user } = useAuth();
  const [isHydrated, setIsHydrated] = useState(false);
  const [signals, setSignals] = useState<Signal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [signalsError, setSignalsError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterState>({
    status: 'ALL',
    type: 'ALL',
    timeframe: 'ALL',
    sortBy: 'NEWEST'
  });
  const [metrics, setMetrics] = useState({
    totalActive: 0,
    avgConfidence: 0
  });

  const liveTickerSymbols = [
  'BTC/USD', 'ETH/USD', 'EUR/USD', 'GBP/USD',
  'XRP/USD', 'ADA/USD', 'SOL/USD', 'MATIC/USD'];


  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Load signals from database with free user limitation
  useEffect(() => {
    const loadSignalsFromDatabase = async () => {
      try {
        setIsLoading(true);
        setSignalsError(null);

        console.log('🔄 Loading signals from database', isFreeUser ? '(Free user - limited to 2 signals)' : '');

        // Fetch signals from database
        const dbSignals = await tradingSignalsService.getActiveSignals({
          assetClass: filters.type === 'ALL' ? undefined : filters.type,
          timeframe: filters.timeframe === 'ALL' ? undefined : filters.timeframe
        });

        // Transform database signals to component format
        let transformedSignals: Signal[] = dbSignals.map((signal: any) => ({
          id: signal.id,
          pair: signal.asset_symbol,
          type: signal.direction as 'BUY' | 'SELL',
          entryPrice: signal.entry_price,
          targetPrice: signal.target_price,
          stopLoss: signal.stop_loss,
          confidence: signal.confidence_score,
          timeframe: signal.timeframe,
          status: signal.is_active ? 'ACTIVE' : 'COMPLETED',
          timestamp: signal.created_at
        }));

        // Limit to 2 signals for free users
        if (isFreeUser) {
          transformedSignals = transformedSignals.slice(0, 2);
          console.log('📊 Free user: Limited to 2 signals');
        }

        setSignals(transformedSignals);

        // Fetch metrics
        const metricsData = await tradingSignalsService.getSignalMetrics();
        setMetrics(metricsData);

        console.log(`✅ Loaded ${transformedSignals.length} signals from database`);
      } catch (error: any) {
        console.error('Error loading signals from database:', error);
        setSignalsError(
          'Unable to load trading signals from database. Please try again later.'
        );
      } finally {
        setIsLoading(false);
      }
    };

    if (isHydrated) {
      loadSignalsFromDatabase();
    }
  }, [isHydrated, filters.type, filters.timeframe, isFreeUser]);

  // Real-time subscription for signal updates
  useEffect(() => {
    if (!isHydrated) return;

    console.log('🔔 Setting up real-time signal subscriptions');

    const unsubscribeSignals = realtimeService.subscribeToSignals(
      (signal, event) => {
        console.log(`📡 Real-time signal ${event}:`, signal);

        if (event === 'INSERT') {
          setSignals((current) => {
            const exists = current?.some((s) => s?.id === signal?.id);
            if (exists) return current;

            const newSignal: Signal = {
              id: signal.id,
              pair: signal.asset_symbol,
              type: signal.direction as 'BUY' | 'SELL',
              entryPrice: signal.entry_price,
              targetPrice: signal.target_price,
              stopLoss: signal.stop_loss,
              confidence: signal.confidence_score,
              timeframe: signal.timeframe,
              status: (signal.is_active ? 'ACTIVE' : 'COMPLETED') as 'ACTIVE' | 'COMPLETED' | 'STOPPED',
              timestamp: signal.created_at
            };

            let updatedSignals = [newSignal, ...(current || [])];
            
            // Limit to 2 signals for free users
            if (isFreeUser) {
              updatedSignals = updatedSignals.slice(0, 2);
            }

            return updatedSignals;
          });
        } else if (event === 'UPDATE') {
          setSignals((current) =>
          current?.map((s) =>
          s?.id === signal?.id ?
          {
            ...s,
            entryPrice: signal.entry_price,
            targetPrice: signal.target_price,
            stopLoss: signal.stop_loss,
            confidence: signal.confidence_score,
            status: signal.is_active ? 'ACTIVE' : 'COMPLETED'
          } :
          s
          ) || []
          );
        } else if (event === 'DELETE') {
          setSignals((current) =>
          current?.filter((s) => s?.id !== signal?.id) || []
          );
        }
      },
      { isActive: true }
    );

    return () => {
      console.log('🔕 Cleaning up real-time signal subscriptions');
      unsubscribeSignals();
    };
  }, [isHydrated, isFreeUser]);

  // Real-time subscription for community activity
  useEffect(() => {
    if (!isHydrated) return;

    console.log('🔔 Setting up real-time community activity subscriptions');

    const unsubscribeCommunity = realtimeService.subscribeToCommunityActivity(
      (activity, event) => {
        if (event === 'INSERT') {
          console.log('📡 New community activity:', activity);
        }
      }
    );

    return () => {
      console.log('🔕 Cleaning up real-time community activity subscriptions');
      unsubscribeCommunity();
    };
  }, [isHydrated]);

  // Real-time subscription for portfolio changes
  useEffect(() => {
    if (!isHydrated || !user?.id) return;

    console.log('🔔 Setting up real-time portfolio subscriptions for user:', user.id);

    const unsubscribePortfolio = realtimeService.subscribeToPortfolio(
      user.id,
      (holding, event) => {
        console.log(`📡 Portfolio ${event}:`, holding);
        if (event === 'INSERT' || event === 'UPDATE') {
          console.log('💼 Portfolio updated');
        }
      }
    );

    return () => {
      console.log('🔕 Cleaning up real-time portfolio subscriptions');
      unsubscribePortfolio();
    };
  }, [isHydrated, user?.id]);

  const handleFilterChange = useCallback((newFilters: FilterState) => {
    setFilters(newFilters);
  }, []);

  const performanceMetrics = [
  {
    label: 'Win Rate',
    value: '67%',
    change: '+2.3%',
    isPositive: true,
    icon: 'ChartBarIcon'
  },
  {
    label: 'Avg Confidence',
    value: `${metrics.avgConfidence}%`,
    change: '+1.5%',
    isPositive: true,
    icon: 'SparklesIcon'
  },
  {
    label: 'Active Signals',
    value: metrics.totalActive.toString(),
    change: `+${Math.floor(metrics.totalActive / 3)}`,
    isPositive: true,
    icon: 'BoltIcon'
  },
  {
    label: 'Active Traders',
    value: '50,000+',
    change: '+12.4%',
    isPositive: true,
    icon: 'UsersIcon'
  }];


  const activities = [
  {
    id: '1',
    user: 'Sarah Chen',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_15dba332b-1763294945056.png",
    avatarAlt: 'Professional headshot of Asian woman with long dark hair in business attire',
    action: 'closed',
    signal: 'BTC/USD',
    profit: 3.45,
    timestamp: '2 minutes ago'
  },
  {
    id: '2',
    user: 'Michael Rodriguez',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_143b978a3-1763294952544.png",
    avatarAlt: 'Professional headshot of Hispanic man with short black hair in navy suit',
    action: 'opened',
    signal: 'ETH/USD',
    timestamp: '5 minutes ago'
  },
  {
    id: '3',
    user: 'Emily Watson',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b6143575-1763294522920.png",
    avatarAlt: 'Professional headshot of Caucasian woman with blonde hair in white blouse',
    action: 'closed',
    signal: 'EUR/USD',
    profit: 2.18,
    timestamp: '8 minutes ago'
  },
  {
    id: '4',
    user: 'David Kim',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1bd15b436-1763300581767.png",
    avatarAlt: 'Professional headshot of Asian man with glasses in gray blazer',
    action: 'opened',
    signal: 'GBP/USD',
    timestamp: '12 minutes ago'
  },
  {
    id: '5',
    user: 'Jessica Martinez',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1600971db-1763295869326.png",
    avatarAlt: 'Professional headshot of Hispanic woman with curly brown hair in blue blazer',
    action: 'closed',
    signal: 'ADA/USD',
    profit: 5.67,
    timestamp: '15 minutes ago'
  }];


  if (!isHydrated || isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-24 pb-16 px-4 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="animate-pulse">
              <div className="h-12 bg-muted rounded w-64 mb-8"></div>
              <div className="h-96 bg-muted rounded"></div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (signalsError) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-24 pb-16 px-4 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="bg-card border border-warning/50 rounded-lg p-8">
              <div className="flex items-start space-x-4">
                <Icon name="ExclamationTriangleIcon" size={48} className="text-warning flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-xl font-heading font-bold text-foreground mb-2">
                    Unable to Load Signals
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {signalsError}
                  </p>
                  <button
                    onClick={() => window.location.reload()}
                    className="inline-flex items-center justify-center px-6 py-3 text-sm font-heading font-semibold text-primary-foreground bg-primary rounded-lg hover:scale-105 transition-all duration-200"
                  >
                    <Icon name="ArrowPathIcon" size={20} className="mr-2" />
                    Retry
                  </button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-24 pb-16 px-4 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Free User Notice Banner */}
          {isFreeUser && (
            <div className="mb-8 bg-primary/10 border border-primary/30 rounded-lg p-6">
              <div className="flex items-start space-x-4">
                <Icon name="InformationCircleIcon" size={32} className="text-primary flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-lg font-heading font-bold text-foreground mb-2">
                    Free Plan - Limited Access
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    You're viewing a limited preview with 2 active signals. Upgrade to Pro or Premium for unlimited signals, real-time updates, and advanced AI insights.
                  </p>
                  <Link
                    href="/pricing"
                    className="inline-flex items-center justify-center px-6 py-3 text-sm font-heading font-semibold text-primary-foreground bg-primary rounded-lg hover:scale-105 hover:shadow-cyan-glow transition-all duration-200"
                  >
                    <Icon name="RocketLaunchIcon" size={20} className="mr-2" />
                    Upgrade Now
                  </Link>
                </div>
              </div>
            </div>
          )}

          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div>
                <h1 className="text-3xl sm:text-4xl font-heading font-bold bg-gradient-to-r from-primary via-primary to-secondary bg-clip-text text-transparent mb-2">
                  {isFreeUser ? 'Live Signals Preview' : 'Live Signals Dashboard'}
                </h1>
                <p className="text-base text-muted-foreground">
                  {isFreeUser 
                    ? 'Sample of 2 real-time AI-powered trading signals' :'Real-time AI-powered trading signals from database'}
                </p>
              </div>
              {!isFreeUser && (
                <Link
                  href="/pricing"
                  className="inline-flex items-center justify-center px-6 py-3 text-sm font-heading font-semibold text-primary-foreground bg-primary rounded-lg hover:scale-105 hover:shadow-cyan-glow transition-all duration-200"
                >
                  <Icon name="RocketLaunchIcon" size={20} className="mr-2" />
                  Get Full Access
                </Link>
              )}
            </div>
          </div>

          <div className="mb-8">
            <PerformanceMetrics metrics={performanceMetrics} />
          </div>

          <div className="mb-8">
            <LiveTicker symbols={liveTickerSymbols} />
          </div>

          {!isFreeUser && (
            <div className="mb-8">
              <FilterBar onFilterChange={handleFilterChange} />
            </div>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            <div className="xl:col-span-2">
              <SignalsList signals={signals} filters={isFreeUser ? { status: 'ALL', type: 'ALL', timeframe: 'ALL', sortBy: 'NEWEST' } : filters} />
              
              {/* Free user signal count indicator */}
              {isFreeUser && signals.length > 0 && (
                <div className="mt-6 bg-muted/50 border border-border rounded-lg p-4 text-center">
                  <p className="text-sm text-muted-foreground">
                    Showing <span className="font-semibold text-foreground">{signals.length} of 2</span> free signals
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Upgrade to see all {metrics.totalActive}+ active signals
                  </p>
                </div>
              )}
            </div>

            <div className="xl:col-span-1">
              <CommunityActivity activities={activities} />
            </div>
          </div>

          <div className="mt-12 bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 rounded-lg p-8 text-center">
            <Icon name="LockClosedIcon" size={48} className="text-primary mx-auto mb-4" />
            <h3 className="text-xl font-heading font-bold text-foreground mb-2">
              {isFreeUser ? 'Unlock All Signals' : 'Unlock Premium Features'}
            </h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
              {isFreeUser 
                ? 'Get unlimited access to all trading signals, real-time notifications, and advanced AI insights with Pro or Premium plans' 
                : 'Get access to advanced signals, real-time notifications, and exclusive trading insights'}
            </p>
            <Link
              href="/pricing"
              className="inline-flex items-center justify-center px-8 py-3 text-sm font-heading font-semibold text-primary-foreground bg-primary rounded-lg hover:scale-105 hover:shadow-cyan-glow transition-all duration-200"
            >
              View Pricing Plans
            </Link>
          </div>
        </div>
      </main>

      <footer className="bg-card border-t border-border py-8 px-4 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm text-muted-foreground">
            &copy; {new Date().getFullYear()} Signaly. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LiveSignalsDashboardInteractive;